import { Grid, MenuItem } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { MRT_ColumnDef } from "material-react-table"
import { enqueueSnackbar } from "notistack"
import { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router"
import {
    getEditPriceListRoute,
    MERCHANT_ADMIN_CREATE_PRICE_LIST_ROUTE,
    MERCHANT_ADMIN_ROUTE,
} from "../../../../../constants/routes"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { StandardNoResultText } from "../../../../../shared/components/noResult/StandardNoResultText"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { DetailedPriceList } from "../components/DetailedPriceList"
import {
    deletePriceList,
    fetchPriceLists,
    FetchPriceListsResponse,
    PriceListQuery,
    PriceListType,
} from "../priceListApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { DeleteConfirmDialog } from "../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { tailwindPalette } from "../../../../theme/tailwindPalette"

const useStyles = makeStyles()(() => ({
    deleteItem: {
        color: tailwindPalette.red[600],
    },
}))

const selectPriceLists = (data: FetchPriceListsResponse) => data?.priceLists || []

type PriceListsProps = Readonly<{}>

export function PriceLists({}: PriceListsProps) {
    const { classes } = useStyles()
    const { merchantId } = useMerchant()
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()

    const [confirmModalState, setConfirmModalState] = useState<{
        showConfirmModal: boolean
        confirmModalText: string
        itemId: null | number
    }>({
        showConfirmModal: false,
        confirmModalText: "",
        itemId: null,
    })

    const priceLists = useApiQuery(fetchPriceLists, {
        queryName: PriceListQuery.LIST,
        dependencies: {
            merchantId,
        },
        select: selectPriceLists,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(priceLists)

    const columns = useMemo<MRT_ColumnDef<PriceListType>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("name"),
                accessorKey: "name",
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("status"),
                },
                (row) => row.isActive,
            ),
        ],
        [columnFactory, l10n],
    )

    const { mutate: callDeletePriceList } = useApiMutation(deletePriceList, {
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [PriceListQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message: l10n.getString("price-list-delete-success-message"),
            })
        },
    })
    const handleDeleteClick = useCallback(() => {
        if (confirmModalState.itemId != null) {
            callDeletePriceList({ id: confirmModalState.itemId })
        } else {
            throw new Error("Price list id is undefined")
        }
    }, [confirmModalState.itemId, callDeletePriceList])

    return (
        <>
            <LocalizedStrict id="price-lists-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.MERCHANT_PRICE_LIST}
                    title="Price Lists"
                    rightElement={
                        <LocalizedStrict id="price-lists-create-button">
                            <StandardButton onClick={() => navigate(MERCHANT_ADMIN_CREATE_PRICE_LIST_ROUTE)}>
                                Create price list
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-products-header">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-price-lists">
                            <Breadcrumb>Price Lists</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Grid container direction="column">
                {/* {priceLists.data?.length === 0 && (
                    <Grid container justifyContent={"center"} rowGap="1rem">
                        <StandardNoResultText noResultText="There are no price lists. Please create you first price list" />
                        <Grid item>
                            <LocalizedStrict id="price-lists-create-button">
                                <StandardButton> Create price list</StandardButton>
                            </LocalizedStrict>
                        </Grid>
                    </Grid>
                )} */}

                <ApiQueryTable
                    columns={columns}
                    initialState={{ density: "spacious" }}
                    renderDetailPanel={({ row }) => {
                        return <DetailedPriceList id={row.original.id} />
                    }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <MenuItem
                            key={`edit-${row.original.id}`}
                            onClick={() => {
                                navigate(getEditPriceListRoute(row.original.id))
                                closeMenu()
                            }}
                        >
                            {l10n.getString("edit")}
                        </MenuItem>,
                        <MenuItem
                            key={`delete-${row.original.id}`}
                            className={classes.deleteItem}
                            onClick={() => {
                                setConfirmModalState({
                                    showConfirmModal: true,
                                    confirmModalText: `${row.original.name}`,
                                    itemId: row.original.id,
                                })

                                closeMenu()
                            }}
                        >
                            {l10n.getString("delete")}
                        </MenuItem>,
                    ]}
                />
            </Grid>

            {confirmModalState.showConfirmModal && (
                <DeleteConfirmDialog
                    typeOfEntity={l10n.getString("price-list-delete")}
                    entityName={confirmModalState.confirmModalText}
                    handleClick={handleDeleteClick}
                    onClose={() =>
                        setConfirmModalState({
                            showConfirmModal: false,
                            confirmModalText: "",
                            itemId: null,
                        })
                    }
                    open={confirmModalState.showConfirmModal}
                />
            )}
        </>
    )
}
